export enum FieldDataType {
  Number = 2,
  Boolean = 3,
  Decimal = 4,
  DateTime = 5,
  TimeStamp = 6,
  String = 13,
  Lookup = 17,
  Text = 24
}

export enum ObjectTypeFilter {
  All = 'all',
  Table = 'table',
  View = 'view'
}

export interface TreeNode {
  id: string;
  name: string;
  displayName: string;
  type: 'object' | 'field';
  icon: string;
  dataType?: string;
  isExpanded: boolean;
  isPrimaryKey?: boolean;
  isRequired?: boolean;
  isLookup?: boolean;
  lookupTarget?: string;
  tooltip?: string; // Pre-computed tooltip for performance
  children?: TreeNode[];
  fieldData?: any;
  objectType?: number; // Table or View indicator
  fieldCount?: number; // Number of fields in the object
}

export interface QueryExecutionResponse {
  success: boolean;
  data: any[];
  metadata: {
    rowCount: number;
    executionTime: number;
    hasMore: boolean;
  };
  error?: string;
  statusCode?: string;
  recordAffected?: number;
  totalRecords?: number;
  totalExecutionTime?: number;
}

export interface LookupDetails {
  targetObjectName: string;
  targetDisplayField: string;
}

export interface Field {
  id: string;
  name: string;
  displayName: string;
  dataType: FieldDataType | string;
  isPrimaryKey: boolean;
  isForeignKey?: boolean;
  isRequired?: boolean;
  isLookup?: boolean;
  lookupDetails?: LookupDetails | any;
  defaultValue?: any;
}

export interface AppObject {
  id: string;
  name: string;
  displayName: string;
  type?: 'Table' | 'View';
  fields: Field[];
}

export interface SchemaData {
  appObjects: AppObject[];
  lastUpdated?: Date;
}

export interface SqlValidationResult {
  valid: boolean;
  errors: string[];
  warnings?: string[];
  summary?: string;
  suggestion?: string;
}

export interface ValidationError {
  message: string;
  line?: number;
  column?: number;
  severity: 'error' | 'warning';
}

export interface UIQueryParameter {
  name: string;
  value: any;
  type: 'text' | 'number' | 'date' | 'lookup';
  required: boolean;
  options?: any[];
}

export interface VisualField {
  id: string;
  fieldName: string;
  alias?: string;
  aggregate: 'None' | 'COUNT' | 'SUM' | 'AVG' | 'MIN' | 'MAX';
  selected: boolean;
}

export interface VisualFilter {
  id: string;
  fieldName: string;
  operator: string;
  value: string;
  conjunction: 'AND' | 'OR';
}

export interface VisualSort {
  id: string;
  fieldName: string;
  direction: 'ASC' | 'DESC';
}

export interface VisualGroup {
  id: string;
  fieldName: string;
}

export enum MatchStrategy {
  PREFIX = 'prefix',
  CONTAINS = 'contains',
  FUZZY = 'fuzzy'
}

export interface SQLContext {
  afterDot: boolean;
  dotTableOrAlias?: string;
  afterFrom: boolean;
  afterJoin: boolean;
  afterAs: boolean;
  afterOn: boolean;
  inWhere: boolean;
  inOrderBy: boolean;
  inGroupBy: boolean;
  inHaving: boolean;
  isTypingKeyword: boolean;
  lastKeyword?: string;
}

export interface WordInfo {
  word: string;
  startColumn: number;
  endColumn: number;
}

export interface ParsedField {
  raw: string;
  expression: string;
  alias: string | null;
  isAggregate: boolean;
  aggregateFunction: string | null;
  innerField: string | null;
}

export interface SplitCondition {
  condition: string;
  conjunction?: 'AND' | 'OR';
}

export interface FromClauseInfo {
  tableName: string;
  alias?: string;
}

export enum GridUpdateState {
  IDLE = 'idle',
  UPDATING_FROM_GRID = 'updating_from_grid',
  EXECUTING_QUERY = 'executing_query'
}

/**
 * SQL Operators for UI
 */
export const SQL_OPERATORS = [
  { value: '=', label: 'Equals (=)', operatorNumber: 3 },
  { value: '!=', label: 'Not Equals (!=)', operatorNumber: 8 },
  { value: '>', label: 'Greater Than (>)', operatorNumber: 1 },
  { value: '>=', label: 'Greater Than or Equal (>=)', operatorNumber: 9 },
  { value: '<', label: 'Less Than (<)', operatorNumber: 2 },
  { value: '<=', label: 'Less Than or Equal (<=)', operatorNumber: 10 },
  { value: 'LIKE', label: 'Contains (LIKE)', operatorNumber: 11 },
  { value: 'IN', label: 'In (IN)', operatorNumber: 4 },
  { value: 'NOT IN', label: 'Not In (NOT IN)', operatorNumber: 5 },
  { value: 'IS NULL', label: 'Is Null', operatorNumber: 6 },
  { value: 'IS NOT NULL', label: 'Is Not Null', operatorNumber: 7 }
] as const;

/**
 * Aggregate functions
 */
export const AGGREGATE_FUNCTIONS = [
  'None', 'COUNT', 'SUM', 'AVG', 'MIN', 'MAX',
  'GROUP_CONCAT', 'STRING_AGG', 'ARRAY_AGG'
] as const;

/**
 * RelationalOperator enum mapping
 */
export const OPERATOR_MAP: { [key: string]: number } = {
  '>': 1, '<': 2, '=': 3, 'IN': 4, 'NOT IN': 5,
  'IS NULL': 6, 'IS NOT NULL': 7, '!=': 8, '<>': 8,
  '>=': 9, '<=': 10, 'LIKE': 11, 'NOT LIKE': 12
} as const;

/**
 * ConjunctionClause enum mapping
 */
export const CONJUNCTION_MAP: { [key: string]: number } = {
  'AND': 1, 'OR': 2, 'AND NOT': 3, 'OR NOT': 4, 'IN': 5
} as const;

/**
 * FilterValueType enum mapping
 */
export const VALUE_TYPE_MAP: { [key: string]: number } = {
  'literal': 1, 'parameter': 2, 'property': 3, 'global': 4
} as const;

/**
 * Reserved SQL keywords (not table aliases)
 */
export const RESERVED_KEYWORDS = new Set([
  'as', 'on', 'inner', 'left', 'right', 'full', 'outer', 'join',
  'where', 'group', 'order', 'having', 'limit', 'offset', 'union',
  'select', 'from', 'insert', 'update', 'delete', 'by', 'and', 'or',
  'not', 'in', 'like', 'between', 'is', 'null'
]);

/**
 * SQL Keywords for completion
 */
export const SQL_KEYWORDS = [
  'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT', 'FULL', 'OUTER',
  'ON', 'GROUP', 'BY', 'ORDER', 'HAVING', 'LIMIT', 'OFFSET',
  'INSERT', 'INTO', 'VALUES', 'UPDATE', 'SET', 'DELETE',
  'AS', 'AND', 'OR', 'NOT', 'IN', 'LIKE', 'BETWEEN', 'IS', 'NULL',
  'COUNT', 'SUM', 'AVG', 'MAX', 'MIN', 'DISTINCT',
  'ASC', 'DESC', 'CASE', 'WHEN', 'THEN', 'ELSE', 'END',
  'UNION', 'ALL', 'EXISTS', 'ANY', 'SOME'
] as const;

/**
 * Multi-word keyword mappings
 */
export const MULTI_WORD_KEYWORDS: Record<string, string> = {
  'ORDER': 'ORDER BY',
  'GROUP': 'GROUP BY'
};

/**
 * High priority keywords for auto-completion
 */
export const HIGH_PRIORITY_KEYWORDS = new Set([
  'SELECT', 'FROM', 'WHERE', 'JOIN', 'INNER', 'LEFT', 'RIGHT',
  'GROUP', 'ORDER', 'HAVING'
]);

/**
 * Keyword typing patterns for fuzzy matching
 */
export const KEYWORD_PATTERNS = new Set([
  'inn', 'inne', 'whe', 'wher', 'se', 'sel', 'sele',
  'fr', 'fro', 'gro', 'grou', 'order', 'left', 'right'
]);